import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ChevronLeft, ChevronRight, CalendarDays, CalendarRange, RefreshCw } from 'lucide-react';
import { MonthView, WeekView } from '@/components/calendar/CalendarGrids';
import { DayPanel, AgendaList } from '@/components/calendar/EventLists';
import { EventDialog } from '@/components/calendar/EventDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { EVENT_TYPES, EVENT_TYPE_META, iso, addDays, startOfWeek } from '@/constants/calendarMeta';
import { usePersistedState } from '@/hooks/usePersistedState';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function TeamCalendarPage() {
  const [meta, setMeta] = useState({ types: EVENT_TYPES, can_manage: false, me: {}, today: iso(new Date()) });
  const [view, setView] = usePersistedState('calendar:view', 'month');
  const [typeFilter, setTypeFilter] = usePersistedState('calendar:type', 'all');
  const [anchor, setAnchor] = useState(new Date());
  const [selected, setSelected] = useState(iso(new Date()));
  const [events, setEvents] = useState([]);
  const [agenda, setAgenda] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [dialogDate, setDialogDate] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const range = useMemo(() => {
    if (view === 'week') {
      const start = startOfWeek(anchor);
      return { from: iso(addDays(start, -7)), to: iso(addDays(start, 14)) };
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    return { from: iso(addDays(first, -10)), to: iso(addDays(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0), 12)) };
  }, [anchor, view]);

  const loadMeta = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/calendar/meta`, authHeader());
      setMeta(res.data);
    } catch (e) {
      toast.error('Failed to load calendar settings');
    }
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from_date: range.from, to_date: range.to };
      if (typeFilter !== 'all') params.type = typeFilter;
      const [list, up] = await Promise.all([
        axios.get(`${API}/calendar/events`, { ...authHeader(), params }),
        axios.get(`${API}/calendar/upcoming`, { ...authHeader(), params: { days: 30 } }),
      ]);
      setEvents(list.data.events || []);
      setAgenda((up.data.events || []).filter((e) => typeFilter === 'all' || e.type === typeFilter));
    } catch (e) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [range.from, range.to, typeFilter]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const canEdit = (ev) => meta.can_manage || ev.created_by_id === meta.me?.id;

  const saveEvent = async (form, existing) => {
    const payload = {
      title: form.title, date: form.date, end_date: form.end_date || '',
      type: form.type, start_time: form.start_time || '', notes: form.notes || '', scope: form.scope,
    };
    try {
      if (existing) {
        await axios.put(`${API}/calendar/events/${existing.id}`, payload, authHeader());
        toast.success('Event updated');
      } else {
        await axios.post(`${API}/calendar/events`, payload, authHeader());
        toast.success('Event created');
      }
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save event');
      throw e;
    }
  };

  const removeEvent = async (ev) => {
    setPendingDelete(ev);
  };

  const confirmDelete = async () => {
    const ev = pendingDelete;
    setPendingDelete(null);
    if (!ev) return;
    try {
      await axios.delete(`${API}/calendar/events/${ev.id}`, authHeader());
      setEvents((prev) => prev.filter((e) => e.id !== ev.id));
      setAgenda((prev) => prev.filter((e) => e.id !== ev.id));
      toast.success('Event deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete event');
    }
  };

  const openNew = (date) => { setEditEvent(null); setDialogDate(date || selected); setDialogOpen(true); };
  const openEdit = (ev) => { setEditEvent(ev); setDialogDate(ev.date); setDialogOpen(true); };

  const shift = (dir) => setAnchor((prev) => (view === 'week'
    ? addDays(prev, dir * 7)
    : new Date(prev.getFullYear(), prev.getMonth() + dir, 1)));

  const label = view === 'week'
    ? `${startOfWeek(anchor).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} – ${addDays(startOfWeek(anchor), 6).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
    : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  return (
    <ResponsiveLayout>
      <div className="space-y-5" data-testid="team-calendar-page">
        <SectionHeader
          icon={CalendarDays}
          title="Team Calendar"
          subtitle="One shared schedule — meetings, events, training and leave"
          action={(
            <Button onClick={() => openNew(selected)}
              className="bg-gradient-to-r from-[#E63946] to-[#F77F00] text-white hover:opacity-90"
              data-testid="calendar-new-btn">
              <Plus size={16} className="mr-1.5" /> New Event
            </Button>
          )}
        />

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] p-1">
            <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))]" data-testid="calendar-prev">
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 text-sm font-medium text-[hsl(var(--text-primary))] min-w-[9rem] text-center" data-testid="calendar-label">{label}</span>
            <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))]" data-testid="calendar-next">
              <ChevronRight size={16} />
            </button>
          </div>

          <Button variant="outline" size="sm" onClick={() => { setAnchor(new Date()); setSelected(meta.today); }} data-testid="calendar-today-btn">
            Today
          </Button>

          <div className="flex gap-1.5">
            {[{ id: 'month', label: 'Month', icon: CalendarDays }, { id: 'week', label: 'Week', icon: CalendarRange }].map((v) => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${view === v.id
                  ? 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/40'
                  : 'border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:bg-white/5'}`}
                data-testid={`calendar-view-${v.id}`}>
                <v.icon size={14} /> {v.label}
              </button>
            ))}
          </div>

          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[9.5rem] h-9" data-testid="calendar-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {(meta.types || EVENT_TYPES).map((t) => (
                <SelectItem key={t} value={t} data-testid={`calendar-filter-${t}`}>{EVENT_TYPE_META[t]?.label || t}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button onClick={loadEvents} className="p-2 rounded-lg border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:bg-white/5"
            title="Refresh" data-testid="calendar-refresh">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <span className="text-xs text-[hsl(var(--text-muted))]" data-testid="calendar-count">{events.length} events in view</span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_20rem] gap-4">
          <div className="space-y-4">
            {view === 'month' ? (
              <MonthView year={anchor.getFullYear()} month={anchor.getMonth()} events={events}
                today={meta.today} selected={selected} onSelectDate={setSelected} onOpenEvent={openEdit} />
            ) : (
              <WeekView anchorDate={anchor} events={events} today={meta.today}
                selected={selected} onSelectDate={setSelected} onOpenEvent={openEdit} />
            )}
            <DayPanel date={selected} events={events} canEdit={canEdit}
              onEdit={openEdit} onDelete={removeEvent} onAdd={openNew} />
          </div>
          <AgendaList events={agenda} canEdit={canEdit} onEdit={openEdit} onDelete={removeEvent} />
        </div>
      </div>

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSave={saveEvent}
        event={editEvent}
        defaultDate={dialogDate}
        canManage={meta.can_manage}
      />

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="event-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription>
              &quot;{pendingDelete?.title}&quot; will be removed from the team calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="event-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="event-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResponsiveLayout>
  );
}
